// „Warstwy" — the mixer (SPEC-redesign §5.7, D-7, D-i, ruling 8, 15).
//
// Two rules decide everything in this file. **A switch is the voice mute**: it
// writes `cisza`, the footer counter reads `cisza`, and the „Komunikaty ZTM"
// switch and the `ZTM·K` tag on the axis are two doors onto one state. Giving
// the switches their own storage would be the third mechanism the brief warns
// against, and it is exactly what makes the prototype lose a resident's choices
// on the way out (`prototype-dossier.md` §9.9).
//
// And **only the four sources we have** (D-7): the prototype lists nine, five of
// which the app cannot fetch. We do not advertise data we do not have.
import type { Snapshot } from '../data/data.ts';
import { band, freshness, ztmStamp } from '../derive/freshness.ts';
import { clockLabel } from '../shared/format.ts';
import type { Item, Voice } from '../shared/model.ts';
import { viewState, type ViewState } from '../state/viewstate.ts';

type LayerGroup = 'jadę' | 'dzieje się' | 'nie działa';

export type Layer = {
  voice: Voice;
  group: LayerGroup;
  /** „Pojazdy ZTM" — what the resident switches. */
  name: string;
  /** `ZTM`, `POZNAN.PL`, `AQUANET` — the mono tag the metadata opens with. */
  tag: string;
};

/** The four, grouped as the prototype groups them. Its „warunki" group has nothing left in it. */
export const LAYERS: readonly Layer[] = [
  { voice: 'ztm', group: 'jadę', name: 'Pojazdy ZTM', tag: 'ZTM' },
  // `ZTM·K`, as the prototype tags it and as `footer.ts` already tags the
  // same voice: with both reading `ZTM` the two „jadę" rows were
  // indistinguishable in their metadata.
  { voice: 'ztmk', group: 'jadę', name: 'Komunikaty ZTM', tag: 'ZTM·K' },
  { voice: 'pl', group: 'dzieje się', name: 'Wydarzenia', tag: 'POZNAN.PL' },
  { voice: 'aq', group: 'nie działa', name: 'Awarie wody', tag: 'AQUANET' },
];

/**
 * A source that never answered and a source whose answer has gone old say
 * **different** things (§7.2): one has nothing, the other has something it can
 * no longer vouch for. Neither is ever called a failure while its rows are on
 * screen — the row is dated instead, and only the source that stopped
 * answering is named (Decision 37, #83).
 *
 * The same sentence for all four, because a screen with nothing on it has the
 * same thing to say whoever was meant to fill it.
 */
const DEAD_SENTENCE = 'Nic jeszcze nie mamy. Sprawdzamy dalej.';

/** What each voice's rows are, in the words its own sentence uses. */
const STALE_SENTENCE: Record<Voice, string> = {
  ztm: 'ZTM nie odpowiada. To ostatnie pozycje.',
  ztmk: 'ZTM nie odpowiada. To ostatnie komunikaty.',
  pl: 'poznan.pl nie odpowiada. To ostatnie wydarzenia.',
  aq: 'Aquanet nie odpowiada. To ostatnie zgłoszenia.',
};

/**
 * Answering, just slow. The source is *not* named as failing — it is answering
 * — so the line says what we are doing about it (§9.3 row 17a, #127).
 */
const OLD_SENTENCE: Record<Voice, string> = {
  ztm: 'Czekamy na odpowiedź ZTM.',
  ztmk: 'Czekamy na odpowiedź ZTM.',
  pl: 'Czekamy na odpowiedź poznan.pl.',
  aq: 'Czekamy na odpowiedź Aquanetu.',
};

/** `pojazd / pojazdy / pojazdów`, the prototype's own rule (§7.2). */
export function pojazdyLabel(n: number): string {
  if (n === 1) return '1 pojazd';
  const last = n % 10;
  const teens = n % 100;
  const few = last >= 2 && last <= 4 && !(teens >= 12 && teens <= 14);
  return `${n} ${few ? 'pojazdy' : 'pojazdów'}`;
}

type Reading = {
  /** The middle of the `TAG · … · N dziś` line — what this source is doing. */
  part: string;
  sentence: string | null;
  bad: boolean;
  /** Only a current source is worth counting: „4 dziś" beside „dane z 13:40" would date itself wrong. */
  live: boolean;
};

const quiet = (part: string, sentence: string | null = null): Reading => ({
  part,
  sentence,
  bad: false,
  live: false,
});
const stale = (at: string, voice: Voice): Reading => ({
  part: `dane z ${at}`,
  sentence: STALE_SENTENCE[voice],
  bad: true,
  live: false,
});
const DEAD: Reading = { part: 'nie odpowiada', sentence: DEAD_SENTENCE, bad: true, live: false };

/**
 * „Pojazdy ZTM" wears the kreska's reading, so the two can never print two
 * hours for one feed (#218): the feed's own stamp while ZTM answers, the
 * health's clock once it stopped (#75).
 */
function ztmReading(snapshot: Snapshot, now: number): Reading {
  const f = freshness(snapshot, now);
  switch (f.kind) {
    case 'offline':
      return { part: f.sentence, sentence: null, bad: true, live: false };
    case 'starting':
      return quiet('łączę się…');
    case 'live':
      return { part: `na żywo, ${f.short} temu`, sentence: null, bad: false, live: true };
    case 'ageing': {
      const old = band(now - ztmStamp(snapshot), true) === 'old';
      return quiet(`dane z ${f.short}`, old ? OLD_SENTENCE.ztm : null);
    }
    case 'stale':
      return stale(f.short, 'ztm');
    case 'dead':
      break;
  }
  return DEAD;
}

/**
 * What one source's row says about itself, before the counts are added.
 *
 * The clock decides the age and `/api/health` decides whether the source may be
 * named: past ten minutes a source that is answering is waited for, and only
 * one that stopped answering is named (Decision 37, #83, #127). The hour is the
 * source's own fetch time, and a source that never answered has no hour to
 * print at all.
 */
function reading(voice: Voice, snapshot: Snapshot, now: number): Reading {
  if (voice === 'ztm') return ztmReading(snapshot, now);
  if (!snapshot.serverReachable) {
    return { part: 'brak połączenia z serwerem', sentence: null, bad: true, live: false };
  }
  const { state, updatedAt } = snapshot.health[voice];
  // Answering with nothing fetched yet is one poll of skew, and a dead source
  // and a starting one must never read the same (D-2, §9.3 row 16).
  if (state === 'starting' || (state !== 'down' && updatedAt === 0)) return quiet('łączę się…');

  const at = clockLabel(updatedAt);
  switch (band(updatedAt ? now - updatedAt : null, state !== 'down')) {
    case 'live':
      return { part: `sprawdzono ${at}`, sentence: null, bad: false, live: true };
    case 'ageing':
      return quiet(`dane z ${at}`);
    case 'old':
      return quiet(`dane z ${at}`, OLD_SENTENCE[voice]);
    case 'stale':
      return stale(at, voice);
    case 'dead':
      break;
  }
  return DEAD;
}

export type LayerRow = {
  layer: Layer;
  on: boolean;
  /** `ZTM · sprawdzono 13:59 · 1 dziś` — the terse line under the name. */
  meta: string;
  /** The whole sentence, when there is a failure to explain. Null otherwise. */
  sentence: string | null;
  /** Brick: the source is stale or down (§7.2). */
  bad: boolean;
};

/**
 * One row per source. The rows are deliberately **not** one template: in the
 * prototype „Pojazdy ZTM" carries a live reading („ZTM · na żywo, 6 s temu ·
 * 2 pojazdy moich linii") while the others carry „TAG · sprawdzono HH:MM ·
 * N dziś", and copying that verbatim is the rule (§0).
 *
 * `marked` is what the wheel WOULD draw with the silences lifted —
 * `derive().markedUnsilenced`, already filtered to the radius (§4.5, D-4), and
 * by construction a superset of what the disc shows. The vehicle count is taken
 * from it and never from the raw snapshot, so this screen can never name
 * vehicles the disc would refuse to draw (prototype inconsistency 13); it names
 * what each switch brings back, which is the count's own question (#163).
 */
export function layerRows(
  snapshot: Snapshot,
  view: ViewState,
  marked: readonly Item[],
  now: number,
): LayerRow[] {
  return LAYERS.map((layer) => {
    const on = !view.muted.includes(layer.voice);
    const { part, sentence, bad, live } = reading(layer.voice, snapshot, now);
    const parts: string[] = [layer.tag, part];

    // Only a current picture is counted. „4 dziś" beside „dane z 13:40" would
    // be a count of what the source knew twenty minutes ago, read as today's.
    if (live) {
      if (layer.voice === 'ztm') {
        const mine = marked.filter(
          (i) => i.kind === 'vehicle' && i.lines?.some((l) => view.lines.includes(l)),
        ).length;
        parts.push(`${pojazdyLabel(mine)} moich linii`);
      } else {
        const kinds: Record<Voice, Item['kind']> = {
          ztm: 'vehicle',
          ztmk: 'notice',
          pl: 'event',
          aq: 'alert',
        };
        parts.push(`${snapshot.items.filter((i) => i.kind === kinds[layer.voice]).length} dziś`);
      }
      // the one extra the prototype gives the calendar, verbatim
      if (layer.voice === 'pl') parts.push('odległości od środka');
    }

    return { layer, on, meta: parts.join(' · '), sentence, bad };
  });
}

/** How many of the four are unmuted — the footer's `n / 4` and this screen's header. */
export const countOn = (view: ViewState): number =>
  LAYERS.filter((l) => !view.muted.includes(l.voice)).length;

/** Where distances are measured from, in the footer note's words. */
function fromLabel(view: ViewState): string {
  if (view.centre.kind === 'me') return 'Odległości od Twojej pozycji.';
  const name = view.centre.kind === 'picked' ? view.centre.name : 'Stary Rynek';
  return `Odległości od: ${name}.`;
}

function switchEl(row: LayerRow): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'layers__row';
  b.setAttribute('role', 'switch');
  b.setAttribute('aria-checked', String(row.on));
  // The switch is named by its source and *described* by its state. Folding the
  // metadata into the name would make „Pojazdy ZTM" a differently named control
  // every ten seconds — one no resident and no test could address (§8).
  b.setAttribute('aria-label', row.layer.name);
  const describedBy = `layer-${row.layer.voice}-state`;
  b.setAttribute('aria-describedby', describedBy);

  const words = document.createElement('span');
  words.className = 'layers__words';
  words.id = describedBy;

  const name = document.createElement('span');
  name.className = 'layers__name';
  name.textContent = row.layer.name;
  // the visible name is already the accessible one, and reading it twice is
  // what „wł." beside „włączone" would be
  name.setAttribute('aria-hidden', 'true');

  const meta = document.createElement('span');
  meta.className = row.bad ? 'layers__meta layers__meta--bad' : 'layers__meta';
  meta.textContent = row.meta;
  words.append(name, meta);

  if (row.sentence) {
    const sentence = document.createElement('span');
    // Brick is for a screen with nothing on it, or for a source that stopped
    // answering; data that is merely old is not wrong (Decision 37).
    sentence.className = row.bad ? 'layers__sentence layers__sentence--bad' : 'layers__sentence';
    sentence.textContent = row.sentence;
    words.append(sentence);
  }

  const pill = document.createElement('span');
  pill.className = row.on ? 'layers__pill layers__pill--on' : 'layers__pill';
  pill.textContent = row.on ? 'wł.' : 'wył.';
  // the pill repeats `aria-checked`; a screen reader reading „wł." after
  // „włączone" would say the same thing twice
  pill.setAttribute('aria-hidden', 'true');

  b.append(words, pill);
  b.addEventListener('click', () => {
    const view = viewState.get();
    const voice = row.layer.voice;
    const muted = view.muted.includes(voice) ? view.muted.filter((v) => v !== voice) : [...view.muted, voice];
    // A switch changes what the view shows; it opens nothing. `'replace'` is
    // what carries the choice past `‹`, Esc and Back (D-b, §4.2).
    viewState.set({ muted }, 'replace');
  });
  return b;
}

/** The screen's body. The `‹ Warstwy` header belongs to the overlay host. */
export function layersScreen(
  snapshot: Snapshot,
  view: ViewState,
  marked: readonly Item[],
  now: number,
): HTMLElement {
  const body = document.createElement('div');
  body.className = 'layers';

  const rows = layerRows(snapshot, view, marked, now);
  let group: LayerGroup | null = null;
  for (const row of rows) {
    if (row.layer.group !== group) {
      group = row.layer.group;
      const head = document.createElement('p');
      head.className = 'layers__group';
      head.textContent = group;
      body.append(head);
    }
    body.append(switchEl(row));
  }

  const foot = document.createElement('p');
  foot.className = 'layers__foot';
  foot.textContent =
    'Warstwy pamiętamy w tej przeglądarce. Kalendarz: Miasto Poznań, poznan.pl. ' +
    `Źródła utrudnień: Aquanet, ZTM Poznań. ${fromLabel(view)}`;
  body.append(foot);

  return body;
}
